import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Building2, BookOpen, MapPin, Users, Search, Plus, X, 
  RefreshCw, ChevronDown, ChevronRight, UserPlus, UserMinus,
  Shield, CheckCircle, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const SuperAdminPanel = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedTab, setSelectedTab] = useState('sponsors');
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectUsers, setObjectUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/superadmin/overview/');
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectUsers = async (type, id) => {
    try {
      const response = await api.get(`/api/superadmin/${type}/${id}/users/`);
      setObjectUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchAllUsers = async (search = '') => {
    try {
      const response = await api.get(`/api/superadmin/users/?search=${search}`);
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching all users:', error);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    if (selectedObject) {
      fetchObjectUsers(selectedTab, selectedObject.id);
    }
  }, [selectedObject, selectedTab]);

  useEffect(() => {
    if (showAddUser) {
      fetchAllUsers(userSearch);
    }
  }, [showAddUser, userSearch]);

  const handleSelectObject = (type, obj) => {
    const id = type === 'sponsors' ? obj.sponsor_id : obj.id;
    setSelectedTab(type);
    setSelectedObject({ ...obj, id, type });
    setShowAddUser(false);
  };

  const handleAddUser = async (userId) => {
    if (!selectedObject) return;
    setActionLoading(true);
    try {
      await api.post(`/api/superadmin/${selectedObject.type}/${selectedObject.id}/users/`, {
        user_ids: [userId]
      });
      toast.success('User added successfully');
      fetchObjectUsers(selectedObject.type, selectedObject.id);
      fetchOverview();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to add user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedObject) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/superadmin/${selectedObject.type}/${selectedObject.id}/users/`, {
        data: { user_ids: [userId] }
      });
      toast.success('User removed successfully');
      fetchObjectUsers(selectedObject.type, selectedObject.id);
      fetchOverview();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Failed to remove user');
    } finally {
      setActionLoading(false);
    }
  };

  const getObjectName = (obj, type) => {
    if (type === 'sponsors') return obj.sponsor_name;
    if (type === 'studies') return obj.title || obj.code;
    if (type === 'sites') return `${obj.site_code} - ${obj.site_name}`;
    return 'Unknown';
  };

  const tabs = [
    { key: 'sponsors', label: 'Sponsors', icon: Building2, color: 'blue' },
    { key: 'studies', label: 'Studies', icon: BookOpen, color: 'green' },
    { key: 'sites', label: 'Sites', icon: MapPin, color: 'purple' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading user manager...</span>
      </div>
    );
  }

  // Check for non-superuser after hooks
  if (user && !user.is_superuser) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <Shield className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 font-medium">Superuser access required</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-amber-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">SuperAdmin Panel</h1>
              <p className="text-gray-600 mt-1">Manage user assignments across sponsors, studies, and sites</p>
            </div>
          </div>
          <button
            onClick={fetchOverview}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-primary-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.total_users || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Sponsors</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.total_sponsors || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <BookOpen className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Studies</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.total_studies || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <MapPin className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Sites</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.total_sites || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Object List */}
        <div className="col-span-4 bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setSelectedTab(tab.key);
                  setSelectedObject(null);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  selectedTab === tab.key
                    ? `text-${tab.color}-600 border-b-2 border-${tab.color}-600 bg-${tab.color}-50`
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4 inline mr-1" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Object List */}
          <div className="max-h-[500px] overflow-y-auto">
            {overview?.[selectedTab]?.map((obj) => {
              const id = selectedTab === 'sponsors' ? obj.sponsor_id : obj.id;
              const isSelected = selectedObject?.id === id;
              return (
                <button
                  key={id}
                  onClick={() => handleSelectObject(selectedTab, obj)}
                  className={`w-full px-4 py-3 flex items-center justify-between text-left border-b hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {getObjectName(obj, selectedTab)}
                    </p>
                    {selectedTab === 'sites' && obj.study__title && (
                      <p className="text-xs text-gray-500 truncate">{obj.study__title}</p>
                    )}
                    {selectedTab === 'studies' && obj.sponsor__sponsor_name && (
                      <p className="text-xs text-gray-500 truncate">{obj.sponsor__sponsor_name}</p>
                    )}
                  </div>
                  <div className="flex items-center ml-2">
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                      {obj.user_count} users
                    </span>
                    {isSelected ? (
                      <ChevronRight className="h-4 w-4 text-primary-600 ml-2" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel - User Management */}
        <div className="col-span-8 bg-white rounded-lg shadow">
          {selectedObject ? (
            <>
              {/* Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {getObjectName(selectedObject, selectedObject.type)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {objectUsers.length} user{objectUsers.length !== 1 ? 's' : ''} assigned
                  </p>
                </div>
                <button
                  onClick={() => setShowAddUser(!showAddUser)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    showAddUser
                      ? 'bg-gray-200 text-gray-700'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {showAddUser ? (
                    <>
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      <span>Add User</span>
                    </>
                  )}
                </button>
              </div>

              {/* Add User Panel */}
              {showAddUser && (
                <div className="px-6 py-4 bg-primary-50 border-b">
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users by email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {allUsers
                      .filter((u) => !objectUsers.some((ou) => ou.id === u.id))
                      .map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{u.email}</p>
                            <p className="text-xs text-gray-500">
                              {u.role} {u.is_superuser && '• Superuser'} {u.is_internal && '• Internal'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleAddUser(u.id)}
                            disabled={actionLoading}
                            className="flex items-center space-x-1 px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                          >
                            <Plus className="h-3 w-3" />
                            <span>Add</span>
                          </button>
                        </div>
                      ))}
                    {allUsers.filter((u) => !objectUsers.some((ou) => ou.id === u.id)).length === 0 && (
                      <p className="text-center text-gray-500 py-4">No users found</p>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned Users List */}
              <div className="max-h-[400px] overflow-y-auto">
                {objectUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Users className="h-12 w-12 mb-3 text-gray-300" />
                    <p>No users assigned</p>
                    <button
                      onClick={() => setShowAddUser(true)}
                      className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                    >
                      Add the first user
                    </button>
                  </div>
                ) : (
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {objectUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {u.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {u.role || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-1">
                              {u.is_superuser && (
                                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs">
                                  Superuser
                                </span>
                              )}
                              {u.is_internal && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                  Internal
                                </span>
                              )}
                              {!u.is_superuser && !u.is_internal && (
                                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                  External
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleRemoveUser(u.id)}
                              disabled={actionLoading}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <UserMinus className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-gray-500">
              <ChevronRight className="h-12 w-12 mb-3 text-gray-300" />
              <p className="text-lg font-medium">Select an object</p>
              <p className="text-sm">Choose a sponsor, study, or site to manage its users</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
